import React, { useRef, useState, useCallback, useEffect } from 'react'
import Globals from 'system/Globals'
import styles from './controller-narrative.module.css'
import mstyles from '../narrative/modules/modules.module.css'
import { ReactComponent as CloseButton } from '../assets/icons/close.svg'
import ControllerModalBrowserChrome from './ControllerModalBrowserChrome'
import { ReactComponent as CaretRight } from '../assets/icons/caret_right.svg'
import { ReactComponent as CaretLeft } from '../assets/icons/caret_left.svg'
import { ReactComponent as CaretUp } from '../assets/icons/caret_up.svg'
import { ReactComponent as CaretDown } from '../assets/icons/caret_down.svg'
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg'
import { ReactComponent as MinusIcon } from '../assets/icons/minus.svg'

function sendUDP(payload) {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.controllerHandler) {
        window.webkit.messageHandlers.controllerHandler.postMessage(
            JSON.stringify({ event: 'UDP', command: JSON.stringify(payload) })
        )
    }
}

function diffType(oldVal, newVal) {
    let i = 0
    while (i < oldVal.length && i < newVal.length && oldVal[i] === newVal[i]) i++
    const deleteCount = oldVal.length - i
    for (let d = 0; d < deleteCount; d++) sendUDP({ type: 'keystroke-special', key: 'Backspace' })
    const newTail = newVal.slice(i)
    for (const c of newTail) sendUDP({ type: 'keystroke', value: c })
}

// Left/right arrow pair — primary value is PowerPoint slide navigation.
// Up/down dropped (scrollbar handles vertical, page bodies rarely respond
// to arrow keys anyway). Other keys come from the native iOS keyboard.
const ARROW_KEYS = [
    { key: 'ArrowLeft',  Icon: CaretLeft },
    { key: 'ArrowRight', Icon: CaretRight },
]

function normalize(s) { return (s || '').replace(/\s+/g, ' ').trim().toLowerCase() }

// Structural hash — excludes mutable text (label, valuePreview, region.name) so
// cosmetic updates don't trigger hold-to-stable resets or false-stable commits.
// Includes only what affects layout: item identity/kind/disabled/parentId and
// region tree shape (which items live where, group composition, nesting).
function structuralHash({ items, regions, unscoped }) {
    const itemsPart = (items || []).map(i =>
        `${i.id}:${i.kind}:${i.disabled ? '1' : '0'}:${i.parentId || ''}`
    ).join('|')

    function regionHash(r) {
        const subs = (r.regions || []).map(regionHash).join(',')
        const groups = (r.groups || []).map(g => {
            const cards = (g.cards || []).map(c =>
                `${c.primaryId}@${(c.itemIds || []).join(':')}`
            ).join(';')
            return `${g.id}[${cards}${g.hasMore ? '+' : ''}]`
        }).join(',')
        return `${r.id}{i:${(r.itemIds || []).join(',')}|g:${groups}|s:${subs}}`
    }
    const regionsPart = (regions || []).map(regionHash).join(';')
    const unscopedPart = (unscoped || []).map(u => u.id).join(',')

    return `${itemsPart}##${regionsPart}##${unscopedPart}`
}

export default function ControllerModalBrowserBrowser({
    items, regions, unscoped, pageTitle,
    focusedId, url, canGoBack, canGoForward,
    scrollTop, scrollHeight, clientHeight,
    debug, isFullscreen, dialog, onClose
}) {
    const prevVal = useRef('')
    const [inputVal, setInputVal] = useState('')
    const [activeInputId, setActiveInputId] = useState(null)
    const [tappedKey, setTappedKey] = useState(null)
    const flashTimer = useRef(null)
    const pageTitleNorm = normalize(pageTitle)
    const pickerListRef = useRef(null)
    const trackRef = useRef(null)
    const draggingRef = useRef(false)
    const [dragPct, setDragPct] = useState(null)
    const [pendingChange, setPendingChange] = useState(false)
    const pendingTimerRef = useRef(null)
    const [dialogBanner, setDialogBanner] = useState(null)
    const dialogTimerRef = useRef(null)
    const lastStructHashRef = useRef('')
    const [displayed, setDisplayed] = useState({ items, regions, unscoped })
    const displayedRef = useRef(displayed)
    displayedRef.current = displayed
    const propsRef = useRef({ items, regions, unscoped })
    propsRef.current = { items, regions, unscoped }

    const markPending = useCallback(() => {
        setPendingChange(true)
        clearTimeout(pendingTimerRef.current)
        pendingTimerRef.current = setTimeout(() => {
            setPendingChange(false)
            setDisplayed({ ...propsRef.current })
        }, 2500)
    }, [])

    // Hold-to-stable: text-only updates (label/valuePreview changes) flow
    // through immediately since they can't shift layout. Structural changes
    // (items added/removed/reordered, region tree reshape) wait for 2-poll
    // agreement on the structural hash before committing.
    useEffect(() => {
        const struct = structuralHash({ items, regions, unscoped })
        const displayedStruct = structuralHash(displayedRef.current)

        if (struct === displayedStruct) {
            // Text-only change — safe to swap without re-running the hold gate.
            setDisplayed({ items, regions, unscoped })
            if (pendingChange) {
                setPendingChange(false)
                clearTimeout(pendingTimerRef.current)
            }
            lastStructHashRef.current = struct
            return
        }

        if (!pendingChange) {
            setDisplayed({ items, regions, unscoped })
            lastStructHashRef.current = struct
            if (pickerListRef.current) pickerListRef.current.scrollTop = 0
            return
        }

        if (struct === lastStructHashRef.current) {
            setDisplayed({ items, regions, unscoped })
            setPendingChange(false)
            clearTimeout(pendingTimerRef.current)
            if (pickerListRef.current) pickerListRef.current.scrollTop = 0
        } else {
            lastStructHashRef.current = struct
        }
    }, [items, regions, unscoped, pendingChange])

    // Show a dismissible banner when the page fires a JS dialog (alert/confirm/prompt).
    // The desktop intercepts these via CDP and surfaces the message here.
    // Auto-dismiss after 6 seconds; tapping Done also clears it.
    const dialogKey = dialog ? `${dialog.type}:${dialog.message}` : null
    useEffect(() => {
        if (!dialogKey) return
        setDialogBanner(dialog)
        clearTimeout(dialogTimerRef.current)
        dialogTimerRef.current = setTimeout(() => setDialogBanner(null), 6000)
        return () => clearTimeout(dialogTimerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogKey])

    // After drag release, keep the thumb latched at dragPct until the polled
    // scrollTop converges. Without this, the thumb falls back to polledFrac on
    // pointer-up, snaps to the stale position, then jumps when the next poll
    // catches up. Convergence threshold is 2% of scrollable range.
    useEffect(() => {
        if (dragPct == null || draggingRef.current) return
        const scrollableNow = Math.max(0, scrollHeight - clientHeight)
        const currentFrac = scrollableNow > 0 ? Math.min(1, Math.max(0, scrollTop / scrollableNow)) : 0
        if (Math.abs(currentFrac - dragPct) < 0.02) setDragPct(null)
    }, [scrollTop, scrollHeight, clientHeight, dragPct])

    const itemsById = {}
    displayed.items.forEach(it => { itemsById[it.id] = it })

    const childrenMap = {}
    displayed.items.forEach(item => {
        if (item.parentId) {
            if (!childrenMap[item.parentId]) childrenMap[item.parentId] = []
            childrenMap[item.parentId].push(item)
        }
    })

    const flash = useCallback((key) => {
        setTappedKey(key)
        clearTimeout(flashTimer.current)
        flashTimer.current = setTimeout(() => setTappedKey(null), 280)
    }, [])

    // Clear the active inline input — dismisses the iOS keyboard and removes
    // the inline editing affordance when the presenter moves on.
    const clearActive = useCallback(() => {
        const el = document.activeElement
        if (el && typeof el.blur === 'function') el.blur()
        setActiveInputId(null)
        prevVal.current = ''
        setInputVal('')
    }, [])

    const onTapItem = useCallback((item) => {
        if (!item || item.disabled) return
        flash(item.id)
        sendUDP({ type: 'click-field', fieldId: item.id })
        markPending()
        clearActive()
    }, [flash, markPending, clearActive])

    // Text-kind items render as inputs directly — no separate label button
    // above them. iOS focuses the input on tap as a native gesture, which
    // surfaces the keyboard and fires onFocus. This handler then mirrors the
    // focus to the kiosk so its corresponding field gets focus too.
    const onFocusInput = useCallback((item) => {
        if (!item || item.disabled) return
        flash(item.id)
        sendUDP({ type: 'focus-field', fieldId: item.id })
        const knownVal = item.valuePreview || ''
        prevVal.current = knownVal
        setInputVal(knownVal)
        setActiveInputId(item.id)
    }, [flash])

    // Tap-outside-to-clear: while a text input is active, any pointerdown that
    // doesn't land on the focused input itself dismisses the keyboard and
    // resets active state. Card/chrome/scroll handlers already call
    // clearActive() on their own; this catches taps on the picker's empty
    // background (between items, page title row, etc.) that have no handler.
    useEffect(() => {
        if (activeInputId == null) return
        const onPointerDownOutside = (e) => {
            const el = document.activeElement
            if (el && el.tagName === 'INPUT' && (el === e.target || el.contains(e.target))) return
            clearActive()
        }
        document.addEventListener('pointerdown', onPointerDownOutside)
        return () => document.removeEventListener('pointerdown', onPointerDownOutside)
    }, [activeInputId, clearActive])

    const onTapKey = useCallback((key) => {
        flash(`key:${key}`)
        sendUDP({ type: 'keystroke-special', key })
    }, [flash])

    const onTapChrome = useCallback((type) => {
        flash(`chrome:${type}`)
        sendUDP({ type })
        markPending()
        clearActive()
    }, [flash, markPending, clearActive])

    const onClearChrome = useCallback((clearType) => {
        sendUDP({ type: 'modal-browser-clear', clearType })
    }, [sendUDP])

    const onTapScroll = useCallback((delta, targetId, axis) => {
        flash(`scroll:${targetId || 'page'}:${delta > 0 ? 'fwd' : 'back'}`)
        const payload = { type: 'modal-browser-scroll', delta }
        if (targetId) payload.targetId = targetId
        if (axis) payload.axis = axis
        sendUDP(payload)
        clearActive()
    }, [flash, clearActive])

    const onTapClose = useCallback(() => {
        flash('close')
        clearActive()
        onClose()
    }, [flash, clearActive, onClose])

    const onInput = useCallback((e) => {
        const next = e.target.value
        diffType(prevVal.current, next)
        prevVal.current = next
        setInputVal(next)
    }, [])

    const scrollable = Math.max(0, scrollHeight - clientHeight)
    const polledFrac = scrollable > 0 ? Math.min(1, Math.max(0, scrollTop / scrollable)) : 0
    // While dragging, use local position so the thumb tracks the finger without
    // stutter from polled updates lagging behind the user's input.
    const thumbFrac = dragPct != null ? dragPct : polledFrac
    const thumbPct = thumbFrac * 100

    const pctFromY = (clientY) => {
        const track = trackRef.current
        if (!track) return 0
        const rect = track.getBoundingClientRect()
        return Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
    }

    const onThumbDown = (e) => {
        if (scrollable <= 0) return
        e.preventDefault()
        draggingRef.current = true
        const startPct = pctFromY(e.clientY)
        setDragPct(startPct)
        sendUDP({ type: 'modal-browser-scroll', to: Math.round(startPct * scrollable) })

        const onMove = (ev) => {
            if (!draggingRef.current) return
            ev.preventDefault()
            const pct = pctFromY(ev.clientY)
            setDragPct(pct)
            sendUDP({ type: 'modal-browser-scroll', to: Math.round(pct * scrollable) })
        }
        const onUp = () => {
            draggingRef.current = false
            // Keep dragPct latched at the released position — the convergence
            // effect below clears it once the polled scrollTop catches up.
            // Prevents a snap-back to the stale polled position during the
            // ~400ms gap before the kiosk's new scrollTop reaches us.
            document.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerup', onUp)
            document.removeEventListener('pointercancel', onUp)
        }
        document.addEventListener('pointermove', onMove)
        document.addEventListener('pointerup', onUp)
        document.addEventListener('pointercancel', onUp)
    }

    function renderItemCard(itemId) {
        const item = itemsById[itemId]
        if (!item) return null

        // Submit items bound to a text input render as inline pills inside
        // that input's card — skip them here to avoid a duplicate card.
        if (item.kind === 'submit' && item.parentId && itemsById[item.parentId]?.kind === 'text') return null

        // Text inputs render directly — the input itself is the tap target.
        // No separate label button; placeholder carries the label, valuePreview
        // shows as a faded badge on the right so the kiosk's current value is
        // still visible at a glance.
        if (item.kind === 'text') {
            const isFocused = item.id === activeInputId
            const isTapped = tappedKey === item.id
            const submitChildren = (childrenMap[itemId] || []).filter(c => c.kind === 'submit')
            const hasSubmit = submitChildren.length > 0
            return (
                <div
                    key={item.id}
                    style={{
                        ...s.card,
                        ...(isFocused ? s.cardFocused : {}),
                        ...(isTapped ? s.cardActive : {}),
                        ...(item.disabled ? s.cardDisabled : {}),
                        position: 'relative',
                        background: 'transparent',
                        ...(hasSubmit ? { display: 'flex', alignItems: 'center' } : {}),
                    }}
                >
                    <div style={hasSubmit ? s.textCardInputWrap : undefined}>
                        <input
                            type="text"
                            value={isFocused ? inputVal : ''}
                            placeholder={item.label}
                            onFocus={() => onFocusInput(item)}
                            onInput={onInput}
                            onChange={() => {}}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    sendUDP({ type: 'keystroke-special', key: 'Enter' })
                                    markPending()
                                }
                            }}
                            style={s.textCardInput}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            disabled={item.disabled}
                        />
                        {!isFocused && item.valuePreview && (
                            <span style={s.textCardPreview}>{item.valuePreview}</span>
                        )}
                    </div>
                    {hasSubmit && submitChildren.map(sub => (
                        <button
                            key={sub.id}
                            style={{
                                ...s.textCardSubmitPill,
                                ...(tappedKey === sub.id ? s.textCardSubmitPillActive : {}),
                                ...(sub.disabled ? { opacity: 0.4, cursor: 'default' } : {}),
                            }}
                            onClick={(e) => { e.stopPropagation(); onTapItem(sub) }}
                        >
                            {sub.label}
                        </button>
                    ))}
                </div>
            )
        }

        // Select elements render as a labeled card with option chips below —
        // one chip per <option>. Tapping a chip sends select-option UDP so the
        // kiosk sets el.value and fires change/input events. The currently
        // selected option gets the focused chip style.
        if (item.kind === 'select') {
            const opts = item.options || []
            const selectedOpt = opts.find(o => o.selected)
            const isTapped = tappedKey === item.id
            return (
                <div
                    key={item.id}
                    style={{
                        ...s.card,
                        ...(isTapped ? s.cardActive : {}),
                        ...(item.disabled ? s.cardDisabled : {}),
                    }}
                >
                    <div style={s.cardHeader}>
                        <span style={s.cardLabel}>{item.label}</span>
                        {selectedOpt && (
                            <span style={s.cardPreview}>{selectedOpt.label}</span>
                        )}
                    </div>
                    {opts.length > 0 && (
                        <div style={s.cardChildren}>
                            {opts.map(opt => (
                                <button
                                    key={opt.value}
                                    style={{
                                        ...s.childChip,
                                        ...(opt.selected ? s.childChipFocused : {}),
                                        ...(tappedKey === `${item.id}:${opt.value}` ? s.childChipActive : {}),
                                        ...(item.disabled ? s.childChipDisabled : {}),
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        flash(`${item.id}:${opt.value}`)
                                        sendUDP({ type: 'select-option', fieldId: item.id, value: opt.value })
                                        markPending()
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )
        }

        const children = childrenMap[itemId] || []
        const isFocused = item.id === focusedId
        const isTapped = tappedKey === item.id
        return (
            <div
                key={item.id}
                style={{
                    ...s.card,
                    ...(item.kind === 'submit' ? s.cardSubmit : {}),
                    ...(isFocused ? s.cardFocused : {}),
                    ...(isTapped ? s.cardActive : {}),
                    ...(item.disabled ? s.cardDisabled : {}),
                }}
            >
                <div style={s.cardHeader} onClick={() => onTapItem(item)}>
                    <span style={s.cardLabel}>{item.label}</span>
                    {item.valuePreview != null && item.valuePreview !== '' && (
                        <span style={s.cardPreview}>{item.valuePreview}</span>
                    )}
                </div>
                {children.length > 0 && (
                    <div style={s.cardChildren}>
                        {children.map(child => (
                            <button
                                key={child.id}
                                style={{
                                    ...s.childChip,
                                    ...(child.id === focusedId ? s.childChipFocused : {}),
                                    ...(tappedKey === child.id ? s.childChipActive : {}),
                                    ...(child.disabled ? s.childChipDisabled : {}),
                                }}
                                onClick={(e) => { e.stopPropagation(); onTapItem(child) }}
                            >
                                {child.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    // Auto-width pill rendering for items in landmark regions (header / nav /
    // footer). Used by renderRegion when region.kind is one of those — saves
    // vertical space vs the full-width renderItemCard for utility links that
    // don't need it.
    function renderPill(itemId) {
        const item = itemsById[itemId]
        if (!item) return null

        if (item.kind === 'submit' && item.parentId && itemsById[item.parentId]?.kind === 'text') return null

        // Text-kind pills render as inline inputs sized to claim a chunk of the
        // pill row. flexGrow lets the input expand within the wrap container
        // when there's space; minWidth keeps it usable when crowded.
        if (item.kind === 'text') {
            const isFocused = item.id === activeInputId
            const isTapped = tappedKey === item.id
            return (
                <div
                    key={item.id}
                    style={{
                        ...s.pillTextWrap,
                        ...(isFocused ? s.pillFocused : {}),
                        ...(isTapped ? s.pillActive : {}),
                        ...(item.disabled ? s.pillDisabled : {}),
                        background: 'transparent',
                    }}
                >
                    <input
                        type="text"
                        value={isFocused ? inputVal : ''}
                        placeholder={item.label}
                        onFocus={() => onFocusInput(item)}
                        onInput={onInput}
                        onChange={() => {}}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                sendUDP({ type: 'keystroke-special', key: 'Enter' })
                                markPending()
                            }
                        }}
                        style={s.pillTextInput}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        disabled={item.disabled}
                    />
                    {!isFocused && item.valuePreview && (
                        <span style={s.pillTextPreview}>{item.valuePreview}</span>
                    )}
                </div>
            )
        }

        const isFocused = item.id === focusedId
        const isTapped = tappedKey === item.id
        return (
            <div
                key={item.id}
                style={{
                    ...s.pill,
                    ...(item.kind === 'submit' ? s.pillSubmit : {}),
                    ...(isFocused ? s.pillFocused : {}),
                    ...(isTapped ? s.pillActive : {}),
                    ...(item.disabled ? s.pillDisabled : {}),
                }}
                onClick={() => onTapItem(item)}
            >
                <span style={s.pillLabel}>{item.label}</span>
            </div>
        )
    }

    function renderRowGroup(group) {
        if (!group.cards || group.cards.length === 0) return null
        return (
            <div key={group.id} style={s.rowGroupOuter}>
                <div style={s.rowGroupTrack}>
                    {group.cards.map((card, idx) => {
                        const primary = itemsById[card.primaryId]
                        if (!primary) return null
                        const subItems = (card.itemIds || [])
                            .filter(id => id !== card.primaryId)
                            .map(id => itemsById[id])
                            .filter(Boolean)
                        const isFocused = card.itemIds.includes(focusedId)
                        const isTapped = tappedKey === primary.id
                        return (
                            <div
                                key={`${group.id}-${idx}-${card.primaryId}`}
                                style={{
                                    ...s.rowCard,
                                    ...(isFocused ? s.rowCardFocused : {}),
                                    ...(isTapped ? s.rowCardActive : {}),
                                    ...(primary.disabled ? s.rowCardDisabled : {}),
                                }}
                                onClick={() => onTapItem(primary)}
                            >
                                <div style={s.rowCardLabel}>{primary.label}</div>
                                {subItems.length > 0 && (
                                    <div style={s.rowCardSubs}>
                                        {subItems.map(sub => (
                                            <button
                                                key={sub.id}
                                                style={{
                                                    ...s.rowCardSubBtn,
                                                    ...(sub.id === focusedId ? s.rowCardSubBtnFocused : {}),
                                                    ...(tappedKey === sub.id ? s.rowCardSubBtnActive : {}),
                                                    ...(sub.disabled ? s.rowCardSubBtnDisabled : {}),
                                                }}
                                                onClick={(e) => { e.stopPropagation(); onTapItem(sub) }}
                                            >
                                                {sub.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                    {group.hasMore && (
                        <button
                            className="mb-more-icon"
                            style={{
                                ...s.rowMoreChip,
                                ...(tappedKey === `scroll:${group.containerId}:fwd` ? s.rowMoreChipActive : {}),
                            }}
                            onClick={() => onTapScroll(240, group.containerId, 'x')}
                            aria-label="More"
                        >
                            <CaretRight />
                        </button>
                    )}
                </div>
            </div>
        )
    }

    function renderFormItem(itemId) {
        const item = itemsById[itemId]
        if (!item) return null

        // Standalone submit: right-aligned pill below the fields
        if (item.kind === 'submit' && !(item.parentId && itemsById[item.parentId]?.kind === 'text')) {
            const isTapped = tappedKey === item.id
            return (
                <div key={item.id} style={s.formSubmitRow}>
                    <button
                        style={{
                            ...s.formSubmitPill,
                            ...(isTapped ? s.textCardSubmitPillActive : {}),
                            ...(item.disabled ? { opacity: 0.4, cursor: 'default' } : {}),
                        }}
                        onClick={() => onTapItem(item)}
                    >
                        {item.label}
                    </button>
                </div>
            )
        }

        // Text field: flat row — form group border is the container, no card chrome
        if (item.kind === 'text') {
            const isFocused = item.id === activeInputId
            const isTapped = tappedKey === item.id
            const submitChildren = (childrenMap[itemId] || []).filter(c => c.kind === 'submit')
            const hasSubmit = submitChildren.length > 0
            return (
                <div
                    key={item.id}
                    style={{
                        ...s.formFieldRow,
                        ...(isFocused ? s.formFieldRowFocused : {}),
                        ...(isTapped ? s.formFieldRowActive : {}),
                        ...(item.disabled ? s.cardDisabled : {}),
                        ...(hasSubmit ? { display: 'flex', alignItems: 'center' } : {}),
                    }}
                >
                    <div style={hasSubmit ? s.textCardInputWrap : undefined}>
                        <input
                            type="text"
                            value={isFocused ? inputVal : ''}
                            placeholder={item.label}
                            onFocus={() => onFocusInput(item)}
                            onInput={onInput}
                            onChange={() => {}}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    sendUDP({ type: 'keystroke-special', key: 'Enter' })
                                    markPending()
                                }
                            }}
                            style={s.textCardInput}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            disabled={item.disabled}
                        />
                        {!isFocused && item.valuePreview && (
                            <span style={s.textCardPreview}>{item.valuePreview}</span>
                        )}
                    </div>
                    {hasSubmit && submitChildren.map(sub => (
                        <button
                            key={sub.id}
                            style={{
                                ...s.textCardSubmitPill,
                                ...(tappedKey === sub.id ? s.textCardSubmitPillActive : {}),
                                ...(sub.disabled ? { opacity: 0.4, cursor: 'default' } : {}),
                            }}
                            onClick={(e) => { e.stopPropagation(); onTapItem(sub) }}
                        >
                            {sub.label}
                        </button>
                    ))}
                </div>
            )
        }

        return renderItemCard(itemId)
    }

    function renderRegion(region, depth, inheritedPill = false) {
        const isFormKind = region.kind === 'form'
        const showName = region.name && normalize(region.name) !== pageTitleNorm
        const headingStyle = depth === 0 ? s.sectionHeading : s.subSectionHeading
        // Nameless wrapper regions don't bump depth — children render at this level
        const childDepth = showName ? depth + 1 : depth
        // Landmark regions where items are typically short utility links —
        // render as auto-width pills in a flex-wrap container instead of
        // full-width vertical cards. The flag propagates to descendants so a
        // <section> nested inside a <footer> still gets pill rendering
        // (UDXD's footer has section.contactInfo + section#footerMenu inside).
        // Sub-regions and detected row-groups still render via their normal handlers.
        const isPillKind = inheritedPill
            || region.kind === 'header'
            || region.kind === 'nav'
            || region.kind === 'footer'
        const inner = (
            <>
                {showName && (
                    <div style={headingStyle}>{region.name}</div>
                )}
                {isPillKind && region.itemIds.length > 0 ? (
                    <div style={s.pillRow}>{region.itemIds.map(renderPill)}</div>
                ) : (
                    region.itemIds.map(isFormKind ? renderFormItem : renderItemCard)
                )}
                {region.groups.map(renderRowGroup)}
                {region.regions.map(r => renderRegion(r, childDepth, isPillKind))}
            </>
        )
        return (
            <div key={region.id} style={isFormKind ? s.formGroup : s.region}>
                {inner}
            </div>
        )
    }

    const hasContent = displayed.regions.length > 0 || displayed.unscoped.length > 0

    return (
        <div style={s.container}>
            {/* Scoped icon styling for picker/scroll/cluster icons. Chrome icon
                styles live inside ControllerModalBrowserChrome. */}
            <style>{`
                .mb-more-icon svg { width: 22px; height: 22px; display: block; }
                .mb-more-icon svg path[stroke] { stroke: rgba(255,255,255,0.6); fill: none; }
                .mb-scroll-icon svg { width: 24px; height: 24px; display: block; margin: 0 auto; }
                .mb-scroll-icon svg path[stroke] { stroke: rgba(255,255,255,0.55); fill: none; }
                .mb-arrow-cluster svg { width: 22px; height: 22px; display: block; }
                .mb-arrow-cluster svg path[stroke],
                .mb-arrow-cluster svg line[stroke] { stroke: #fff; fill: none; }
            `}</style>
            <ControllerModalBrowserChrome
                url={url}
                canGoBack={canGoBack}
                canGoForward={canGoForward}
                isFullscreen={isFullscreen}
                tappedKey={tappedKey}
                onTap={onTapChrome}
                onClear={onClearChrome}
            />

            {/* Page title */}
            {pageTitle && (
                <div style={s.pageTitle}>{pageTitle}</div>
            )}

            {/* Pending-update indicator: pulses while we wait for new items after a tap */}
            {pendingChange && (
                <>
                    <style>{`@keyframes mbPending { 0%{opacity:0.3;transform:scaleX(0.4)} 50%{opacity:1;transform:scaleX(1)} 100%{opacity:0.3;transform:scaleX(0.4)} }`}</style>
                    <div style={s.pendingBar} />
                </>
            )}

            {/* Page alert banner — shown when the site fires alert/confirm/prompt */}
            {dialogBanner && (
                <div style={s.dialogBanner}>
                    <span style={s.dialogBannerLabel}>Page alert</span>
                    <span style={s.dialogBannerMsg}>{dialogBanner.message}</span>
                    <button style={s.dialogBannerDismiss} onClick={() => { sendUDP({ type: 'dismiss-dialog' }); setDialogBanner(null) }}>Done</button>
                </div>
            )}

            {/* Region list + scrollbar */}
            <div style={s.pickerRow}>
                <div ref={pickerListRef} style={{
                    ...s.pickerList,
                    opacity: pendingChange ? 0.55 : 1,
                    transition: 'opacity 0.2s ease',
                }}>
                    {!hasContent && (
                        <div style={s.emptyMsg}>No interactive elements found</div>
                    )}
                    {displayed.regions.map((r, i) => (
                        <React.Fragment key={r.id}>
                            {i > 0 && <div style={s.regionDivider} />}
                            {renderRegion(r, 0)}
                        </React.Fragment>
                    ))}
                    {displayed.unscoped.length > 0 && (
                        <>
                            {displayed.regions.length > 0 && <div style={s.regionDivider} />}
                            <div style={s.region}>
                                {displayed.unscoped.map(renderItemCard)}
                            </div>
                        </>
                    )}
                    {debug && (
                        <pre style={s.debugBox}>{debug}</pre>
                    )}
                </div>

                <div style={s.scrollbar}>
                    <button
                        className="mb-scroll-icon"
                        style={{
                            ...s.scrollBtn,
                            ...(tappedKey === 'scroll:page:back' ? s.scrollBtnActive : {}),
                        }}
                        onClick={() => onTapScroll(-240)}
                        aria-label="Scroll up"
                    ><CaretUp /></button>
                    <div
                        style={s.scrollTrack}
                        ref={trackRef}
                        onPointerDown={onThumbDown}
                    >
                        <div style={{...s.scrollThumb, top: `calc(${thumbFrac * 100}% - ${thumbFrac * 40}px)`}} />
                    </div>
                    <button
                        className="mb-scroll-icon"
                        style={{
                            ...s.scrollBtn,
                            ...(tappedKey === 'scroll:page:fwd' ? s.scrollBtnActive : {}),
                        }}
                        onClick={() => onTapScroll(240)}
                        aria-label="Scroll down"
                    ><CaretDown /></button>
                </div>
            </div>

            {/* Left/right arrow pair for slide nav + zoom buttons. */}
            <div className="mb-arrow-cluster" style={s.arrowCluster}>
                {ARROW_KEYS.map(k => (
                    <button
                        key={k.key}
                        style={{
                            ...s.arrowBtn,
                            ...(tappedKey === `key:${k.key}` ? s.arrowBtnActive : {}),
                        }}
                        onClick={() => onTapKey(k.key)}
                        aria-label={k.key}
                    ><k.Icon /></button>
                ))}
                <button
                    style={{
                        ...s.arrowBtn,
                        ...(tappedKey === 'zoom:out' ? s.arrowBtnActive : {}),
                    }}
                    onClick={() => { flash('zoom:out'); sendUDP({ type: 'modal-browser-zoom-out' }) }}
                    aria-label="Zoom out"
                ><MinusIcon /></button>
                <button
                    style={{
                        ...s.arrowBtn,
                        ...(tappedKey === 'zoom:in' ? s.arrowBtnActive : {}),
                    }}
                    onClick={() => { flash('zoom:in'); sendUDP({ type: 'modal-browser-zoom-in' }) }}
                    aria-label="Zoom in"
                ><PlusIcon /></button>
            </div>

            {/* Standard close button — matches other iPad module pages (see ControllerGalleryPopup) */}
            <span className={styles.joystickClose}>
                <div className={mstyles.closeBtnContainer}>
                    <div
                        className="round-btn"
                        onClick={onTapClose}
                        style={Globals.instance().getThemeStyle(['nav-menu-btn'])}
                    >
                        <CloseButton />
                    </div>
                </div>
            </span>
        </div>
    )
}

const s = {
    container: {
        position: 'fixed', inset: 0, zIndex: 9000,
        background: '#393939',
        display: 'flex', flexDirection: 'column',
        padding: 10,
        boxSizing: 'border-box',
        color: '#fff', fontFamily: 'sans-serif',
        userSelect: 'none',
    },
    pageTitle: {
        fontSize: 18, fontWeight: 600,
        padding: '12px 16px 4px',
        color: '#fff', flexShrink: 0,
    },
    pendingBar: {
        height: 3,
        background: '#ffd250',
        flexShrink: 0,
        margin: '0 12px 4px',
        borderRadius: 2,
        transformOrigin: 'left center',
        animation: 'mbPending 1s ease-in-out infinite',
    },
    pickerRow: {
        flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden',
    },
    pickerList: {
        flex: 1, overflowY: 'scroll',
        padding: '4px 0 12px',
        WebkitOverflowScrolling: 'touch',
    },
    region: {
    },
    formGroup: {
        margin: '8px 12px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.05)',
        overflow: 'hidden',
    },
    // Thin rule between top-level regions to make the header / main / footer
    // bands legible at a glance (especially after the kind-based sort puts
    // them in canonical order regardless of DOM placement).
    regionDivider: {
        height: 1,
        background: 'rgba(255,255,255,0.14)',
        margin: '24px 16px',
    },
    sectionHeading: {
        fontSize: 13, fontWeight: 700,
        color: 'rgba(255,255,255,0.85)',
        padding: '14px 16px 6px',
    },
    subSectionHeading: {
        fontSize: 11, fontWeight: 700,
        color: 'rgba(255,255,255,0.45)',
        letterSpacing: 0.8, textTransform: 'uppercase',
        padding: '10px 16px 4px',
    },
    card: {
        margin: '10px 12px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    cardSubmit: {
        border: '1px solid rgba(68,162,244,0.5)',
        background: 'rgba(68,162,244,0.3)',
    },
    cardFocused: {
        border: '1px solid #4da6ff',
        background: 'rgba(77,166,255,0.1)',
    },
    cardActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.18)',
    },
    cardDisabled: { opacity: 0.3 },
    cardHeader: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px',
        cursor: 'pointer',
    },
    cardLabel: { flex: 1, fontSize: 14 },
    cardPreview: {
        fontSize: 12, color: 'rgba(255,255,255,0.45)',
        maxWidth: 120, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardChildren: {
        display: 'flex', flexWrap: 'wrap', gap: 6,
        padding: '8px 12px 12px',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(0,0,0,0.2)',
    },
    childChip: {
        padding: '7px 14px',
        borderRadius: 20,
        border: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(255,255,255,0.1)',
        color: '#fff', fontSize: 13,
        cursor: 'pointer',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    childChipFocused: {
        border: '1px solid #4da6ff',
        background: 'rgba(77,166,255,0.2)',
    },
    childChipActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.32)',
    },
    childChipDisabled: { opacity: 0.4, cursor: 'default' },
    rowGroupOuter: {
        margin: '4px 0',
    },
    rowGroupTrack: {
        display: 'flex', flexDirection: 'row',
        gap: 10, padding: '4px 12px',
        overflowX: 'auto', overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
    },
    rowCard: {
        flexShrink: 0,
        minWidth: 140, maxWidth: 200,
        padding: '12px 14px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.08)',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 8,
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    rowCardFocused: {
        border: '1px solid #4da6ff',
        background: 'rgba(77,166,255,0.12)',
    },
    rowCardActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.22)',
    },
    rowCardDisabled: { opacity: 0.3 },
    rowCardLabel: {
        fontSize: 13, fontWeight: 600,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    rowCardSubs: {
        display: 'flex', flexDirection: 'column', gap: 5,
    },
    rowCardSubBtn: {
        padding: '6px 10px',
        borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.06)',
        color: '#fff', fontSize: 12,
        cursor: 'pointer', textAlign: 'left',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    rowCardSubBtnFocused: {
        border: '1px solid #4da6ff',
        background: 'rgba(77,166,255,0.18)',
    },
    rowCardSubBtnActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.3)',
    },
    rowCardSubBtnDisabled: { opacity: 0.4, cursor: 'default' },
    rowMoreChip: {
        flexShrink: 0,
        padding: '0 18px',
        borderRadius: 10,
        border: '1px dashed rgba(255,255,255,0.2)',
        background: 'transparent',
        color: 'rgba(255,255,255,0.6)', fontSize: 18,
        cursor: 'pointer',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    rowMoreChipActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.22)',
    },
    emptyMsg: {
        textAlign: 'center', color: 'rgba(255,255,255,0.25)',
        fontSize: 14, padding: 40,
    },
    debugBox: {
        margin: '12px',
        padding: 8,
        background: 'rgba(0,0,0,0.4)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 4,
        fontSize: 10, color: 'rgba(180,255,180,0.7)',
        whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        fontFamily: 'monospace',
    },
    scrollbar: {
        width: 40, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        background: '#555555',
        borderRadius: 999,
        margin: '4px 12px 12px 0',
        overflow: 'hidden',
    },
    scrollBtn: {
        height: 40, flexShrink: 0,
        background: 'none', border: 'none',
        color: 'rgba(255,255,255,0.55)', fontSize: 14,
        cursor: 'pointer',
        transition: 'background 0.15s ease, color 0.15s ease',
    },
    scrollBtnActive: {
        background: 'rgba(255,210,80,0.32)',
        color: '#ffd250',
    },
    scrollTrack: {
        flex: 1, position: 'relative',
        margin: '2px 8px',
        background: 'rgba(0,0,0,0.18)',
        borderRadius: 999,
        touchAction: 'none',
        cursor: 'pointer',
    },
    scrollThumb: {
        position: 'absolute', left: 0, right: 0,
        height: 40,
        background: 'rgba(255,255,255,0.45)',
        borderRadius: 999,
        pointerEvents: 'none',
    },
    // Text-kind item rendered inside a card-shaped wrapper. The input itself
    // is the tappable surface — no separate label button above it. placeholder
    // carries the label so an empty input still tells the user what it does.
    textCardInput: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '14px 14px',
        paddingRight: 90,  // reserve room for the valuePreview badge
        background: 'transparent',
        border: 'none',
        color: '#fff',
        fontSize: 14,
        outline: 'none',
        fontFamily: 'inherit',
    },
    textCardPreview: {
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 12,
        color: 'rgba(255,255,255,0.45)',
        maxWidth: 80,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
    },
    textCardInputWrap: {
        flex: 1,
        position: 'relative',
        minWidth: 0,
    },
    textCardSubmitPill: {
        margin: '8px 10px 8px 4px',
        padding: '0 16px',
        height: 36,
        borderRadius: 18,
        border: '1px solid rgba(68,162,244,0.5)',
        background: 'rgba(68,162,244,0.3)',
        color: '#fff',
        fontSize: 13,
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    textCardSubmitPillActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.3)',
    },
    formFieldRow: {
        position: 'relative',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        transition: 'background 0.15s ease',
    },
    formFieldRowFocused: {
        background: 'rgba(77,166,255,0.08)',
    },
    formFieldRowActive: {
        background: 'rgba(255,210,80,0.1)',
    },
    formSubmitRow: {
        padding: '8px 12px',
        display: 'flex',
        justifyContent: 'center',
    },
    formSubmitPill: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        boxSizing: 'border-box',
        padding: '0 18px',
        height: 40,
        borderRadius: 999,
        border: '1px solid rgba(68,162,244,0.5)',
        background: 'rgba(68,162,244,0.3)',
        color: '#fff',
        fontSize: 13,
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    // Flex-wrap pill row for landmark regions (header / nav / footer).
    // Pills are auto-width — single buttons sit content-sized, multiple short
    // labels flow across one line and wrap to the next when needed.
    pillRow: {
        display: 'flex', flexWrap: 'wrap', gap: 10,
        padding: '10px 12px',
    },
    pill: {
        padding: '8px 14px',
        borderRadius: 18,
        border: '1px solid rgba(255,255,255,0.14)',
        background: 'rgba(255,255,255,0.08)',
        color: '#fff', fontSize: 13,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8,
        maxWidth: '100%',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    pillSubmit: {
        border: '1px solid rgba(68,162,244,0.5)',
        background: 'rgba(68,162,244,0.3)',
    },
    pillFocused: {
        border: '1px solid #4da6ff',
        background: 'rgba(77,166,255,0.2)',
    },
    pillActive: {
        border: '1px solid #ffd250',
        background: 'rgba(255,210,80,0.3)',
    },
    pillDisabled: { opacity: 0.4, cursor: 'default' },
    pillLabel: {
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: 240,
    },
    // Text-kind pill: the input itself, sized as a pill that grows to claim a
    // chunk of the flex-wrap row. valuePreview badge sits to the right inside
    // the wrap when present.
    pillTextWrap: {
        position: 'relative',
        flexGrow: 1,
        flexBasis: 220,
        minWidth: 200,
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 18,
        border: '1px solid rgba(255,255,255,0.4)',
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
        transition: 'background 0.15s ease, border-color 0.15s ease',
    },
    pillTextInput: {
        flex: 1,
        boxSizing: 'border-box',
        padding: '8px 14px',
        background: 'transparent',
        border: 'none',
        color: '#fff',
        fontSize: 13,
        outline: 'none',
        fontFamily: 'inherit',
        minWidth: 0,
    },
    pillTextPreview: {
        paddingRight: 12,
        fontSize: 11,
        color: 'rgba(255,255,255,0.45)',
        maxWidth: 80,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
    },
    arrowCluster: {
        position: 'absolute',
        // 40 scrollbar + 12 right margin + 36 gap (16 base + 20 requested) = 88px
        right: 88,
        bottom: 20,
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        zIndex: 20,
    },
    arrowBtn: {
        width: 44, height: 44,
        background: '#555555',
        border: 'none',
        borderRadius: 999,
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.12s ease',
    },
    arrowBtnActive: {
        background: 'rgba(255,210,80,0.45)',
    },
    dialogBanner: {
        background: '#92400e',
        borderRadius: 6,
        padding: '8px 12px',
        margin: '4px 0 2px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    dialogBannerLabel: {
        fontWeight: 700,
        fontSize: 11,
        color: '#fde68a',
        textTransform: 'uppercase',
        flexShrink: 0,
        letterSpacing: '0.04em',
    },
    dialogBannerMsg: {
        fontSize: 13,
        color: '#fff',
        flex: 1,
    },
    dialogBannerDismiss: {
        background: 'rgba(255,255,255,0.18)',
        border: 'none',
        borderRadius: 4,
        padding: '4px 12px',
        color: '#fff',
        fontSize: 12,
        cursor: 'pointer',
        flexShrink: 0,
        fontFamily: 'inherit',
    },
}
